"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, startTransition, use, useEffect, useRef, useState, } from "react";
import { encodeReply } from "react-server-dom-webpack/client.browser";
import { getRscPayloadFromDocument, render } from "#Runtime.js";
import { useServerComponentContext } from "./ServerComponentContext.js";
import { Uint8ArraySink } from "./Uint8ArraySink.js";
/**
 * Renders a server component on the client. An HTTP POST request will be made
 * to the configured endpoint whenever the component's props change.
 *
 * The payload sent to the server will contain the `type` and `props` passed to
 * this component. The server must render the component and respond with the RSC
 * payload.
 *
 * The RSC payload will be cached in memory on the client. The cache is keyed
 * by the props. This prevents rerendering the server component if it is
 * unmounted and remounted. This enables immediate navigation to a previously
 * rendered server component without rerendering it on the server.
 *
 * This component uses React's Suspense API. You may provide a suspense fallback
 * to display a loading state while the component is rendering.
 */
export const ServerComponent = 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
({ props, suspense, type, }) => {
    const { cache, callServer, endpoint, ssrManifest } = useServerComponentContext();
    const getRscPayloadFromCache = (key) => {
        return cache.has(key) ? new Response(cache.get(key)).body : null;
    };
    const getPromise = async (type, props) => {
        const body = (await encodeReply({ type, props }));
        const key = body;
        let responseBody;
        responseBody = getRscPayloadFromCache(key);
        responseBody !== null && responseBody !== void 0 ? responseBody : (responseBody = getRscPayloadFromDocument(key));
        responseBody !== null && responseBody !== void 0 ? responseBody : (responseBody = (await fetch(endpoint, { body, method: "POST" })).body);
        const [left, right] = responseBody.tee();
        const sink = new Uint8ArraySink();
        left.pipeTo(sink);
        const content = await render(right, { callServer, ssrManifest });
        cache.set(body, sink.data);
        return {
            content,
            key,
            rscPayload: sink.data,
        };
    };
    const [promise, setPromise] = useState(() => getPromise(type, props));
    const didMount = useRef(false);
    useEffect(() => {
        if (!didMount.current)
            return void (didMount.current = true);
        startTransition(() => {
            setPromise(getPromise(type, props));
        });
    }, [type, props]);
    return (_jsx(Suspense, { ...suspense, children: _jsx(Async, { promise: promise }) }));
};
const Async = ({ promise }) => {
    const { content, key, rscPayload } = use(promise);
    return (_jsxs(_Fragment, { children: [content, _jsx("script", { id: key, type: "json", children: JSON.stringify([...rscPayload]) })] }));
};
